import type { CSSProperties, ReactNode } from 'react';
import { ProgressBar } from 'antd-mobile';
import styled, { keyframes } from 'styled-components';
import { r } from '../../theme/riderTheme';
import {
  PageCard,
  PrimaryButton,
  GhostButton,
  ButtonChevron,
} from '../../theme/surfacePrimitives';

export type PackCardTone = 'green' | 'orange' | 'red' | 'grey' | 'black' | 'blue';

export interface PackCardMetric {
  key: string;
  label: string;
  value: ReactNode;
  detail?: ReactNode;
  icon?: ReactNode;
  percent?: number | null;
  tone?: PackCardTone;
}

export interface PackCardNotice {
  tone: 'warning' | 'critical' | 'success';
  icon?: ReactNode;
  content: ReactNode;
}

export interface PackCardQueueItem {
  key: string;
  title: ReactNode;
  detail?: ReactNode;
  badge?: ReactNode;
}

export interface PackCardAction {
  key?: string;
  label: ReactNode;
  onClick?: () => void;
  fill?: 'solid' | 'outline' | 'none';
  color?: 'primary' | 'success' | 'warning' | 'danger' | 'default';
  disabled?: boolean;
}

export interface PackCardChecklistItem {
  key: string;
  label: ReactNode;
  checked: boolean;
}

export interface PackCardEmptyState {
  icon?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: PackCardAction;
}

export interface PackCardRideStatus {
  ready: boolean;
  content: ReactNode;
  icon?: ReactNode;
}

interface DashboardPackCardProps {
  title: ReactNode;
  icon?: ReactNode;
  statusLabel?: ReactNode;
  statusTone?: PackCardTone;
  packName?: ReactNode;
  packMeta?: ReactNode;
  metrics?: PackCardMetric[];
  notice?: PackCardNotice;
  queueTitle?: ReactNode;
  queueItems?: PackCardQueueItem[];
  queueOverflowCount?: number;
  checklist?: PackCardChecklistItem[];
  actions?: PackCardAction[];
  rideStatus?: PackCardRideStatus;
  emptyState?: PackCardEmptyState;
}

interface ToneColors {
  accent: string;     // left rail + progress fill + metric icon
  tileBg: string;     // metric tile background
  tileBorder: string; // metric tile border
  badgeBg: string;    // status pill background
  badgeBorder: string;
  badgeText: string;
}

// Resolves tone → riderTheme-aligned color set. Kept inline so the file stays
// component-only-export friendly.
const cardShell = styled(PageCard)<{ $tone: PackCardTone }>`
  position: relative;
  padding: 16px 16px 14px;
  border-left: 3px solid ${({ theme, $tone }) => toneOf(theme, $tone).accent};
`;

const toneOf = (theme: unknown, tone: PackCardTone): ToneColors => {
  const t = r(theme);
  switch (tone) {
    case 'green':
      return {
        accent: t.accent.limeDeep,
        tileBg: 'rgba(192, 223, 22, 0.06)',
        tileBorder: 'rgba(192, 223, 22, 0.30)',
        badgeBg: 'rgba(0, 185, 70, 0.10)',
        badgeBorder: 'rgba(0, 185, 70, 0.32)',
        badgeText: t.accent.ok,
      };
    case 'orange':
      return {
        accent: '#f59e0b',
        tileBg: 'rgba(245, 158, 11, 0.07)',
        tileBorder: 'rgba(245, 158, 11, 0.30)',
        badgeBg: 'rgba(245, 158, 11, 0.10)',
        badgeBorder: 'rgba(245, 158, 11, 0.35)',
        badgeText: '#b45309',
      };
    case 'red':
      return {
        accent: t.accent.alert,
        tileBg: 'rgba(255, 25, 35, 0.06)',
        tileBorder: 'rgba(255, 25, 35, 0.28)',
        badgeBg: 'rgba(255, 25, 35, 0.08)',
        badgeBorder: 'rgba(255, 25, 35, 0.30)',
        badgeText: t.accent.alert,
      };
    case 'blue':
      return {
        accent: t.accent.info,
        tileBg: 'rgba(0, 215, 255, 0.06)',
        tileBorder: 'rgba(0, 215, 255, 0.30)',
        badgeBg: 'rgba(0, 215, 255, 0.10)',
        badgeBorder: 'rgba(0, 215, 255, 0.32)',
        badgeText: '#0884a0',
      };
    case 'black':
      return {
        accent: t.text.primary,
        tileBg: t.surface.inputIdle,
        tileBorder: t.surface.edge,
        badgeBg: 'rgba(18, 18, 21, 0.06)',
        badgeBorder: t.surface.edgeStrong,
        badgeText: t.text.primary,
      };
    default:
      return {
        accent: t.text.muted,
        tileBg: t.surface.inputIdle,
        tileBorder: t.surface.edge,
        badgeBg: t.surface.inputIdle,
        badgeBorder: t.surface.edge,
        badgeText: t.text.muted,
      };
  }
};

// ProgressBar's --fill-color is set via inline style and doesn't have theme
// context, so we resolve tone → hex eagerly here. Stays in sync with toneOf.
const progressFillFor: Record<PackCardTone, string> = {
  green: '#99b310',
  orange: '#f59e0b',
  red: '#ff1923',
  blue: '#00d7ff',
  black: '#121215',
  grey: 'rgba(18, 18, 21, 0.62)',
};

const noticeOf = (theme: unknown, tone: PackCardNotice['tone']) => {
  const t = r(theme);
  switch (tone) {
    case 'warning':
      return {
        bg: 'rgba(245, 158, 11, 0.06)',
        border: 'rgba(245, 158, 11, 0.28)',
        text: '#b45309',
      };
    case 'critical':
      return {
        bg: 'rgba(255, 25, 35, 0.05)',
        border: 'rgba(255, 25, 35, 0.24)',
        text: t.accent.alert,
      };
    case 'success':
    default:
      return {
        bg: 'rgba(0, 185, 70, 0.06)',
        border: 'rgba(0, 185, 70, 0.28)',
        text: t.accent.ok,
      };
  }
};

const clampPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(100, value));
};

const stagger = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CardShell = cardShell;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
`;

const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
`;

const IconBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border: 1px solid ${({ theme }) => r(theme).accent.limeDeep};
  background: ${({ theme }) => r(theme).surface.raisedSelected};
  color: ${({ theme }) => r(theme).accent.ok};
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
`;

const StatusBadge = styled.span<{ $tone: PackCardTone }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  background: ${({ theme, $tone }) => toneOf(theme, $tone).badgeBg};
  border: 1px solid ${({ theme, $tone }) => toneOf(theme, $tone).badgeBorder};
  color: ${({ theme, $tone }) => toneOf(theme, $tone).badgeText};
`;

const PackSummary = styled.div`
  background: ${({ theme }) => r(theme).surface.inputIdle};
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
`;

const PackName = styled.div`
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.3;
  overflow-wrap: anywhere;
`;

const PackMeta = styled.div`
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  margin-top: 4px;
`;

const MetricGrid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: ${p =>
    p.$count > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  gap: 10px;
  margin-bottom: 14px;
`;

const MetricTile = styled.div<{ $tone: PackCardTone }>`
  min-width: 0;
  min-height: 104px;
  padding: 14px 12px;
  border: 1px solid ${({ theme, $tone }) => toneOf(theme, $tone).tileBorder};
  border-radius: 10px;
  background: ${({ theme, $tone }) => toneOf(theme, $tone).tileBg};
  animation: ${stagger} 320ms cubic-bezier(0.2, 0.8, 0.2, 1) backwards;
`;

const MetricIcon = styled.div<{ $tone: PackCardTone }>`
  display: flex;
  justify-content: center;
  color: ${({ theme, $tone }) => toneOf(theme, $tone).accent};
  font-size: 18px;
  margin-bottom: 6px;
`;

const MetricValue = styled.div`
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.015em;
  text-align: center;
  line-height: 1.2;
`;

const MetricLabel = styled.div`
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  text-align: center;
  margin-top: 4px;
`;

const MetricDetail = styled.div`
  color: ${({ theme }) => r(theme).text.faint};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  text-align: center;
  margin-top: 4px;
`;

const MetricProgress = styled.div`
  margin-top: 10px;
`;

const Notice = styled.div<{ $tone: PackCardNotice['tone'] }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${({ theme, $tone }) => noticeOf(theme, $tone).border};
  background: ${({ theme, $tone }) => noticeOf(theme, $tone).bg};
  color: ${({ theme, $tone }) => noticeOf(theme, $tone).text};
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.4;
`;

const QueueSection = styled.div`
  margin-bottom: 14px;
`;

const QueueHeader = styled.div`
  color: ${({ theme }) => r(theme).text.faint};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const QueueItem = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => r(theme).surface.edge};
  border-radius: 10px;
  background: ${({ theme }) => r(theme).surface.inputIdle};
`;

const QueueText = styled.div`
  min-width: 0;
`;

const QueueTitleEl = styled.div`
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueDetail = styled.div`
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin-top: 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const QueueBadge = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => r(theme).accent.ok};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const OverflowText = styled.div`
  color: ${({ theme }) => r(theme).text.faint};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  text-align: center;
  margin-top: 8px;
`;

const Checklist = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 14px;
`;

const ChecklistItem = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $checked }) =>
      $checked ? r(theme).accent.limeDeep : r(theme).surface.edge};
  background: ${({ theme, $checked }) =>
    $checked ? r(theme).surface.raisedSelected : r(theme).surface.inputIdle};
  color: ${({ theme, $checked }) =>
    $checked ? r(theme).accent.ok : r(theme).text.muted};
  font-family: 'Geist Mono', ui-monospace, monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
`;

const Actions = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: ${p =>
    p.$count > 1 ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)'};
  gap: 8px;
  margin-top: 2px;
  margin-bottom: 14px;

  @media (max-width: 520px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const RideStatus = styled.div<{ $ready: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid
    ${({ $ready }) =>
      $ready ? 'rgba(0, 185, 70, 0.32)' : 'rgba(255, 25, 35, 0.28)'};
  background: ${({ $ready }) =>
    $ready ? 'rgba(0, 185, 70, 0.06)' : 'rgba(255, 25, 35, 0.05)'};
  color: ${({ theme, $ready }) =>
    $ready ? r(theme).accent.ok : r(theme).accent.alert};
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  font-weight: 500;
  text-align: center;
`;

const EmptyState = styled.div`
  padding: 22px 12px;
  text-align: center;
`;

const EmptyIcon = styled.div`
  color: ${({ theme }) => r(theme).text.faint};
  font-size: 32px;
  margin-bottom: 10px;
`;

const EmptyTitle = styled.div`
  color: ${({ theme }) => r(theme).text.primary};
  font-family: 'Geist', sans-serif;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  margin-bottom: 6px;
`;

const EmptyDescription = styled.div`
  color: ${({ theme }) => r(theme).text.muted};
  font-family: 'Geist', sans-serif;
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 16px;
`;

const renderAction = (action: PackCardAction, isPrimary: boolean) => {
  const useGhost = action.fill === 'outline' || action.fill === 'none' || !isPrimary;
  const Btn = useGhost ? GhostButton : PrimaryButton;
  return (
    <Btn
      key={action.key}
      $disabled={action.disabled}
      disabled={action.disabled}
      onClick={action.onClick}
    >
      {useGhost ? (
        <span>{action.label}</span>
      ) : (
        <>
          <span>{action.label}</span>
          <ButtonChevron>→</ButtonChevron>
        </>
      )}
    </Btn>
  );
};

const DashboardPackCard = ({
  title,
  icon,
  statusLabel,
  statusTone = 'grey',
  packName,
  packMeta,
  metrics = [],
  notice,
  queueTitle,
  queueItems = [],
  queueOverflowCount = 0,
  checklist = [],
  actions = [],
  rideStatus,
  emptyState,
}: DashboardPackCardProps) => {
  return (
    <CardShell $tone={statusTone}>
      <Header>
        <TitleLine>
          {icon && <IconBadge>{icon}</IconBadge>}
          <span>{title}</span>
        </TitleLine>
        {statusLabel && (
          <StatusBadge $tone={statusTone}>{statusLabel}</StatusBadge>
        )}
      </Header>

      {packName && (
        <PackSummary>
          <PackName>{packName}</PackName>
          {packMeta && <PackMeta>{packMeta}</PackMeta>}
        </PackSummary>
      )}

      {emptyState && (
        <EmptyState>
          {emptyState.icon && <EmptyIcon>{emptyState.icon}</EmptyIcon>}
          <EmptyTitle>{emptyState.title}</EmptyTitle>
          {emptyState.description && (
            <EmptyDescription>{emptyState.description}</EmptyDescription>
          )}
          {emptyState.action && renderAction(emptyState.action, true)}
        </EmptyState>
      )}

      {metrics.length > 0 && (
        <MetricGrid $count={metrics.length}>
          {metrics.map(metric => {
            const tone = metric.tone ?? statusTone;
            return (
              <MetricTile key={metric.key} $tone={tone}>
                {metric.icon && <MetricIcon $tone={tone}>{metric.icon}</MetricIcon>}
                <MetricValue>{metric.value}</MetricValue>
                <MetricLabel>{metric.label}</MetricLabel>
                {metric.detail && <MetricDetail>{metric.detail}</MetricDetail>}
                {metric.percent !== undefined && metric.percent !== null && (
                  <MetricProgress>
                    <ProgressBar
                      percent={clampPercent(metric.percent)}
                      style={
                        {
                          '--fill-color': progressFillFor[tone],
                          '--track-color': 'rgba(18, 18, 21, 0.06)',
                        } as CSSProperties
                      }
                    />
                  </MetricProgress>
                )}
              </MetricTile>
            );
          })}
        </MetricGrid>
      )}

      {notice && (
        <Notice $tone={notice.tone}>
          {notice.icon}
          <span>{notice.content}</span>
        </Notice>
      )}

      {queueItems.length > 0 && (
        <QueueSection>
          {queueTitle && <QueueHeader>{queueTitle}</QueueHeader>}
          <QueueList>
            {queueItems.map(item => (
              <QueueItem key={item.key}>
                <QueueText>
                  <QueueTitleEl>{item.title}</QueueTitleEl>
                  {item.detail && <QueueDetail>{item.detail}</QueueDetail>}
                </QueueText>
                {item.badge && <QueueBadge>{item.badge}</QueueBadge>}
              </QueueItem>
            ))}
          </QueueList>
          {queueOverflowCount > 0 && (
            <OverflowText>+{queueOverflowCount} more packs</OverflowText>
          )}
        </QueueSection>
      )}

      {checklist.length > 0 && (
        <Checklist>
          {checklist.map(item => (
            <ChecklistItem key={item.key} $checked={item.checked}>
              {item.checked ? '✓' : '○'} {item.label}
            </ChecklistItem>
          ))}
        </Checklist>
      )}

      {actions.length > 0 && (
        <Actions $count={actions.length}>
          {actions.map((action, index) => renderAction(action, index === 0))}
        </Actions>
      )}

      {rideStatus && (
        <RideStatus $ready={rideStatus.ready}>
          {rideStatus.icon}
          <span>{rideStatus.content}</span>
        </RideStatus>
      )}
    </CardShell>
  );
};

export default DashboardPackCard;
